import { useRouteState } from './routeState'
import { useNavigate } from 'react-router-dom'
import { LiveCursors } from './components/LiveCursors'
import { LiveProvider } from './LiveRoom'
import { ResumeSession } from './ResumeSession'
import { ReleaseUpdates } from './components/ReleaseUpdates'
import { WorkspaceHub } from './components/WorkspaceHub'
import { TaskProvider, useTasks } from './TaskProvider'
import { TasksPage } from './components/TasksPage'
import { BiblePeek } from './components/BibleEntryEditor'
import { referenceTargets } from './connectedBible'
import { readBrowserValue, writeBrowserValue } from './browserStorage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCheck,
  ChevronDown,
  Download,
  FileText,
  Flag,
  HelpCircle,
  History,
  PanelLeft,
  PanelRight,
  NotebookPen,
  X,
  BookOpenText,
  Check,
  CloudOff,
  CloudUpload,
  GitFork,
  LayoutGrid,
  LoaderCircle,
  LogOut,
  Moon,
  Play,
  Plus,
  Redo2,
  Search,
  Sprout,
  Sun,
  Undo2,
} from 'lucide-react'
import { LoginScreen } from './components/LoginScreen'
import { PlayPreview } from './components/PlayPreview'
import { ProjectDashboard } from './components/ProjectDashboard'
import { ScreenplayEditor } from './components/ScreenplayEditor'
import { StoryInspector } from './components/StoryInspector'
import { StoryBible } from './components/StoryBible'
import { DraftHistory } from './components/DraftHistory'
import { ProductionExport } from './components/ProductionExport'
import { FindReplace } from './components/FindReplace'
import { ReviewExport } from './components/ReviewExport'
import { GraphWorkspace } from './components/GraphWorkspace'
import { matchFocusId, type StoryMatch } from './findReplace'
import { downloadFullProjectFile, downloadProjectFile } from './projectTransfer'
import { downloadManuscript } from './manuscriptExport'
import {
  autoLayout,
  createNode,
  disconnectEdge as removeEdgeConnection,
  projectIssues,
  removeNode,
  removeStoryElements,
  storyWordCount,
  nodeWordCount,
  sceneSearchText,
  draftStatuses,
} from './model'
import type { EditorView, StoryProject, Theme, RoomUser, TourId } from './types'
import { useAuth } from './useAuth'
import { useProjectPersistence } from './useProjectPersistence'

const isEditableTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null
  return Boolean(
    element?.closest('input, textarea, select, [contenteditable="true"]'),
  )
}

function initialTheme(): Theme {
  const saved = readBrowserValue('bramble:theme')
  if (saved === 'dark' || saved === 'light') return saved
  return 'light'
}

export default function App() {
  const auth = useAuth()
  const [theme, setTheme] = useState<Theme>(initialTheme)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.documentElement.style.colorScheme = theme
    writeBrowserValue('bramble:theme', theme)
  }, [theme])

  if (auth.status === 'checking') {
    return (
      <main className="app-loading">
        <Sprout size={28} />
        <span>Checking the garden gate…</span>
      </main>
    )
  }

  if (auth.status === 'unauthenticated') {
    return (
      <LoginScreen
        theme={theme}
        busy={auth.busy}
        error={auth.error}
        onThemeChange={setTheme}
        onLogin={auth.login}
      />
    )
  }

  return (
    <ResumeSession key={auth.user!.id} user={auth.user!}><LiveProvider key={auth.user!.id} user={auth.user!}><TaskProvider key={auth.user!.id} user={auth.user!}>
    <BrambleEditor
      key={auth.user!.id}
      user={auth.user!}
      onProfile={auth.updateProfile}
      theme={theme}
      onThemeChange={setTheme}
      onLogout={auth.logout}
      onUnauthorized={auth.markUnauthorized}
    />
    </TaskProvider></LiveProvider></ResumeSession>
  )
}

type BrambleEditorProps = {
  user: RoomUser
  onProfile: (body: unknown) => Promise<void>
  theme: Theme
  onThemeChange: (theme: Theme) => void
  onLogout: () => Promise<void>
  onUnauthorized: () => void
}

function BrambleEditor({
  user,
  onProfile,
  theme,
  onThemeChange,
  onLogout,
  onUnauthorized,
}: BrambleEditorProps) {
  const navigate = useNavigate()
  const [routeProject, setRouteProject] = useRouteState<string | null>('project', null)
  const routeLoading = useRef<string | null>(null)
  const [routeError, setRouteError] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState('')
  const downloadBackup = async (value: StoryProject) => {
    setDownloadError('')
    try { await downloadFullProjectFile(value, user.id) }
    catch (e) { setDownloadError(e instanceof Error ? e.message : 'This backup could not be downloaded.') }
  }
  const taskStore = useTasks()
  const persistence = useProjectPersistence(user.id, onUnauthorized)
  const { project, changeProject, syncState } = persistence
  useEffect(() => {
    window.addEventListener(
      'bramble:unauthorized',
      persistence.handleUnauthorized,
    )
    return () =>
      window.removeEventListener(
        'bramble:unauthorized',
        persistence.handleUnauthorized,
      )
  }, [persistence.handleUnauthorized])
  const [biblePeek, setBiblePeek] = useRouteState<string | null>('peek', null)
  const [bibleFocus, setBibleFocus] = useRouteState<string | null>('entry', null)
  const [bibleTourStep, setBibleTourStep] = useState<number | null>(null)
  const handleTourStep = useCallback((id: TourId, index: number | null) => {
    setBibleTourStep(id === 'bible' ? index : null)
  }, [])
  const [showDashboard, setShowDashboard] = useRouteState<boolean>('library', true)
  const [view, setView] = useRouteState<EditorView>('view', 'script', ['script', 'graph', 'bible'])
  const [selectedNodeId, setSelectedNodeId] = useRouteState<string | null>('scene', null)
  const [search, setSearch] = useState('')
  const [previewOpen, setPreviewOpen] = useRouteState<boolean>('play', false)
  const [historyOpen, setHistoryOpen] = useRouteState<boolean>('history', false)
  const [productionExportOpen, setProductionExportOpen] = useRouteState<boolean>('export', false)
  const [findOpen, setFindOpen] = useRouteState<boolean>('find', false)
  const [reviewExportOpen, setReviewExportOpen] = useRouteState<boolean>('review-export', false)
  const [pendingMatch, setPendingMatch] = useState<StoryMatch | null>(null)
  const [previewStartId, setPreviewStartId] = useRouteState<string | null>('play-scene', null)
  const [focusMode, setFocusMode] = useRouteState<boolean>('focus', false)
  const [inspectorOpen, setInspectorOpen] = useRouteState('inspector', window.innerWidth >= 1200, undefined, true)
  const [inspectorTab, setInspectorTab] = useRouteState<'details' | 'review'>('inspector-tab', 'details', ['details', 'review'])
  const [outlineOpen, setOutlineOpen] = useRouteState<boolean>('outline', false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [focusVariables, setFocusVariables] = useState(false)
  const openVariables = () => {
    setFocusVariables(true)
    setView('bible')
    setFocusMode(false)
  }
  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    // Preserve the sender's panel state when a desktop link opens on a phone.
    if (project && !showDashboard && !new URLSearchParams(window.location.search).has('inspector')) setInspectorOpen(inspectorOpen)
  }, [project?.id, showDashboard, inspectorOpen, setInspectorOpen])
  useEffect(() => {
    const fn = (event: Event) => {
      const params = new URLSearchParams(window.location.search)
      for (const [key, value] of Object.entries((event as CustomEvent<Record<string, string | null>>).detail)) value === null ? params.delete(key) : params.set(key, value)
      void navigate({ pathname: '/', search: '?' + params.toString() })
    }
    window.addEventListener('bramble:navigate', fn)
    return () => window.removeEventListener('bramble:navigate', fn)
  }, [navigate])
  useEffect(() => {
    if (!routeProject || routeProject === persistence.activeProjectId || routeLoading.current === routeProject) return
    routeLoading.current = routeProject
    let cancelled = false
    void persistence.openProject(routeProject).then(opened => {
      if (!opened && !cancelled) { setRouteError('This shared project link could not be opened. It may be in Trash or no longer exist.'); setShowDashboard(true); setRouteProject(null) }
    }).catch(() => { if (!cancelled) { setRouteError('This shared project link could not be opened. Try the library or Project Trash.'); setShowDashboard(true); setRouteProject(null) } }).finally(() => { if (routeLoading.current === routeProject) routeLoading.current = null })
    return () => { cancelled = true }
  }, [routeProject, persistence.openProject])
  useEffect(() => {
    if (project && !showDashboard && !routeLoading.current && routeProject !== project.id) setRouteProject(project.id)
  }, [project?.id, showDashboard, routeProject])


  useEffect(() => {
    if (
      !pendingMatch ||
      selectedNodeId !== pendingMatch.nodeId ||
      view !== 'script'
    )
      return
    const element = document.getElementById(matchFocusId(pendingMatch)) as
      HTMLInputElement | HTMLTextAreaElement | null
    if (element) {
      element.focus()
      element.setSelectionRange(pendingMatch.start, pendingMatch.end)
      element.scrollIntoView({ block: 'center' })
    }
    setPendingMatch(null)
  }, [pendingMatch, selectedNodeId, view])

  useEffect(() => {
    const closeMenus = (event: PointerEvent) => {
      document
        .querySelectorAll<HTMLDetailsElement>(
          'details.export-menu[open], details.shortcut-menu[open]',
        )
        .forEach((menu) => {
          if (!menu.contains(event.target as Node)) menu.open = false
        })
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      document
        .querySelectorAll<HTMLDetailsElement>(
          'details.export-menu[open], details.shortcut-menu[open]',
        )
        .forEach((menu) => {
          menu.open = false
          menu.querySelector('summary')?.focus()
        })
    }
    const media = window.matchMedia('(max-width: 1180px)')
    const adaptInspector = () => {
      if (media.matches) setInspectorOpen(false)
    }
    window.addEventListener('pointerdown', closeMenus)
    window.addEventListener('keydown', closeOnEscape)
    media.addEventListener('change', adaptInspector)
    return () => {
      window.removeEventListener('pointerdown', closeMenus)
      window.removeEventListener('keydown', closeOnEscape)
      media.removeEventListener('change', adaptInspector)
    }
  }, [])

  const pastRef = useRef<StoryProject[]>([])
  const futureRef = useRef<StoryProject[]>([])
  const historyGroupRef = useRef<{ key: string; at: number } | null>(null)
  const [, forceHistoryRender] = useState(0)

  useEffect(() => {
    if (!project || selectedNodeId) return
    const savedScene = readBrowserValue('bramble:last-scene:' + project.id)
    setSelectedNodeId(
      project.nodes.some((node) => node.id === savedScene)
        ? savedScene
        : project.startNodeId,
    )
  }, [project, selectedNodeId])

  useEffect(() => {
    if (
      project &&
      selectedNodeId &&
      !showDashboard &&
      project.nodes.some((node) => node.id === selectedNodeId)
    ) {
      writeBrowserValue('bramble:last-scene:' + project.id, selectedNodeId)
      writeBrowserValue('bramble:last-project', project.id)
    }
  }, [project?.id, selectedNodeId, showDashboard])

  useEffect(() => {
    if (!project || !selectedNodeId) return
    if (!project.nodes.some((node) => node.id === selectedNodeId)) {
      setSelectedNodeId(project.startNodeId)
    }
  }, [project, selectedNodeId])

  const commitProject = useCallback(
    (next: StoryProject, historyKey = 'command', recordHistory = true) => {
      if (!project || next === project) return
      if (recordHistory) {
        const now = Date.now()
        const group = historyGroupRef.current
        if (!group || group.key !== historyKey || now - group.at > 650) {
          pastRef.current = [...pastRef.current.slice(-59), project]
        }
        historyGroupRef.current = { key: historyKey, at: now }
        futureRef.current = []
        forceHistoryRender((value) => value + 1)
      }
      changeProject(next)
    },
    [changeProject, project],
  )

  const undo = useCallback(() => {
    if (persistence.liveUndo()) return
    if (!project) return
    const previous = pastRef.current.at(-1)
    if (!previous) return
    pastRef.current = pastRef.current.slice(0, -1)
    futureRef.current = [...futureRef.current.slice(-59), project]
    historyGroupRef.current = null
    changeProject(previous)
    forceHistoryRender((value) => value + 1)
  }, [changeProject, project])

  const redo = useCallback(() => {
    if (persistence.liveRedo()) return
    if (!project) return
    const next = futureRef.current.at(-1)
    if (!next) return
    futureRef.current = futureRef.current.slice(0, -1)
    pastRef.current = [...pastRef.current.slice(-59), project]
    historyGroupRef.current = null
    changeProject(next)
    forceHistoryRender((value) => value + 1)
  }, [changeProject, project])

  const addNode = useCallback(
    (position?: { x: number; y: number }) => {
      if (!project) return
      const selected = project.nodes.find((node) => node.id === selectedNodeId)
      const nextPosition =
        position ??
        (selected
          ? {
              x: selected.position.x + 390,
              y: selected.position.y + selected.choices.length * 80,
            }
          : { x: 90, y: 90 })
      const node = createNode(project, nextPosition)
      const nodes = [...project.nodes, node]
      // New scene is a drafting action. Routes are created explicitly in the document or map.
      commitProject({ ...project, nodes }, 'add-node')
      setSelectedNodeId(node.id)
    },
    [commitProject, project, selectedNodeId],
  )

  const deleteNode = useCallback(
    (nodeId: string) => {
      if (!project || project.nodes.length <= 1) return
      const next = removeNode(project, nodeId)
      commitProject(next, 'delete-node')
      setSelectedNodeId((current) =>
        current === nodeId ? next.startNodeId : current,
      )
    },
    [commitProject, project],
  )

  const deleteNodes = useCallback(
    (nodeIds: string[]) => {
      if (!project || project.nodes.length <= 1) return
      const deleting = new Set(nodeIds)
      let next = project
      for (const nodeId of deleting) {
        if (next.nodes.length <= 1) break
        next = removeNode(next, nodeId)
      }
      if (next === project) return
      commitProject(next, 'delete-node')
      setSelectedNodeId((current) =>
        current && deleting.has(current) ? next.startNodeId : current,
      )
    },
    [commitProject, project],
  )

  const moveNode = useCallback(
    (nodeId: string, position: { x: number; y: number }) => {
      if (!project) return
      commitProject(
        {
          ...project,
          nodes: project.nodes.map((node) =>
            node.id === nodeId ? { ...node, position } : node,
          ),
        },
        'move-node',
      )
    },
    [commitProject, project],
  )

  const connectNodes = useCallback(
    (sourceId: string, sourceHandle: string | null, targetId: string) => {
      if (!project) return
      const next = {
        ...project,
        nodes: project.nodes.map((node) => {
          if (node.id !== sourceId || node.kind === 'ending') return node
          if (sourceHandle === 'continue') return { ...node, nextId: targetId }
          if (sourceHandle?.startsWith('choice:')) {
            const choiceId = sourceHandle.slice('choice:'.length)
            return {
              ...node,
              choices: node.choices.map((choice) =>
                choice.id === choiceId ? { ...choice, targetId } : choice,
              ),
            }
          }
          return node
        }),
      }
      commitProject(next, 'connect')
    },
    [commitProject, project],
  )

  const disconnectEdge = useCallback(
    (edgeId: string) => {
      if (!project) return
      commitProject(removeEdgeConnection(project, edgeId), 'disconnect')
    },
    [commitProject, project],
  )

  const arrange = useCallback(() => {
    if (project) commitProject(autoLayout(project), 'auto-layout')
  }, [commitProject, project])

  const resetEditorSession = useCallback(() => {
    setRouteError(null)
    setFocusVariables(false)
    pastRef.current = []
    futureRef.current = []
    historyGroupRef.current = null
    forceHistoryRender((value) => value + 1)
    setSelectedNodeId(null)
    setSearch('')
    setPreviewOpen(false)
    setHistoryOpen(false)
    setProductionExportOpen(false)
    setFindOpen(false)
    setReviewExportOpen(false)
    setPendingMatch(null)
    setBiblePeek(null)
    setView('script')
    setFocusMode(false)
    setOutlineOpen(false)
    setStatusFilter('all')
    setInspectorTab('details')
  }, [])

  const openProject = useCallback(
    async (projectId: string) => {
      const opened = await persistence.openProject(projectId)
      if (!opened) throw new Error('That project could not be opened.')
      resetEditorSession()
      setShowDashboard(false)
    },
    [persistence.openProject, resetEditorSession],
  )

  const createProject = useCallback(
    async (title: string) => {
      await persistence.createProject(title)
      resetEditorSession()
      setShowDashboard(false)
    },
    [persistence.createProject, resetEditorSession],
  )

  const importProject = useCallback(
    async (projectToImport: StoryProject) => {
      await persistence.importProject(projectToImport)
      resetEditorSession()
      setShowDashboard(false)
    },
    [persistence.importProject, resetEditorSession],
  )

  const showProjectDashboard = useCallback(() => {
    void persistence.flush()
    setPreviewOpen(false)
    setShowDashboard(true)
  }, [persistence.flush])

  const signOut = useCallback(async () => {
    if (await persistence.prepareToLeave()) await onLogout()
    else setShowDashboard(false)
  }, [onLogout, persistence.prepareToLeave])

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (
        showDashboard || taskStore.page ||
        previewOpen ||
        document.querySelector('dialog[open]')
      )
        return
      const mod = event.metaKey || event.ctrlKey
      if (mod && event.key.toLowerCase() === 's') {
        event.preventDefault()
        persistence.flush()
      }
      if (mod && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        if (event.shiftKey) redo()
        else undo()
      }
      if (mod && event.key === '1') {
        event.preventDefault()
        setView('graph')
      }
      if (mod && event.key === '2') {
        event.preventDefault()
        setView('script')
      }
      if (mod && event.key === '3') {
        event.preventDefault()
        setView('bible')
        setFocusVariables(false)
      }
      if (mod && !event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault()
        setFindOpen(true)
      }
      if (mod && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOutlineOpen(true)
        setFocusMode(false)
        requestAnimationFrame(() => searchRef.current?.focus())
      }
      if (mod && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault()
        setView('script')
        setFocusMode((value) => !value)
      }
      if (event.key === 'Escape') {
        setFocusMode(false)
        setOutlineOpen(false)
      }
      if (
        !mod &&
        event.key.toLowerCase() === 'n' &&
        !isEditableTarget(event.target)
      ) {
        event.preventDefault()
        addNode()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [addNode, persistence, previewOpen, redo, showDashboard, undo, taskStore.page])

  const filteredNodes = useMemo(() => {
    if (!project) return []
    const term = search.trim().toLowerCase()
    return project.nodes.filter(
      (node) =>
        (!term || sceneSearchText(node).includes(term)) &&
        (statusFilter === 'all' || (node.status ?? 'draft') === statusFilter),
    )
  }, [project, search, statusFilter])

  const selectScene = (nodeId: string) => {
    setSelectedNodeId(nodeId)
    setOutlineOpen(false)
    if (view === 'bible') setView('script')
  }

  const openReference = (id: string) => {
    if (id.startsWith('scene:')) {
      setBiblePeek(null)
      setSelectedNodeId(id.slice(6))
      setView('script')
    } else setBiblePeek(id)
  }
  const openTaskTarget = async (projectId: string, targetId?: string) => {
    if (targetId) {
      const source = projectId === project?.id ? project : await persistence.projectForExport(projectId)
      if (!referenceTargets(source).some(target => target.id === targetId))
        throw new Error('This entry or scene no longer exists. Its task is kept so you can finish or archive it.')
    }
    if (projectId !== project?.id || showDashboard) await openProject(projectId)
    setShowDashboard(false)
    setBiblePeek(null)
    if (targetId && !targetId.startsWith('scene:')) {
      setView('bible')
      setBibleFocus(targetId)
      setFocusVariables(false)
    }
    else if (targetId) openReference(targetId)
    else setView('script')
  }
  const workspaceHub = (
    <>{downloadError && <div className="backup-error" role="alert">{downloadError}
      {project && <button type="button" onClick={() => { downloadProjectFile(project); setDownloadError('') }}>Download draft with media links</button>}
      <button type="button" aria-label="Dismiss backup error" onClick={() => setDownloadError('')}><X size={16} /></button></div>}
    <ReleaseUpdates save={persistence.flush} syncState={syncState} busy={taskStore.busy} /><LiveCursors /><WorkspaceHub
      key="workspace-hub"
      user={user}
      onProfile={onProfile}
      project={project}
      projects={persistence.projects || []}
      context={
        taskStore.page ? 'tasks' : showDashboard
          ? 'library'
          : view === 'graph'
            ? 'map'
            : view === 'bible'
              ? 'bible'
              : 'writing'
      }
      onTourSection={(id) => {
        taskStore.closePage()
        if (id === 'library') showProjectDashboard()
        else if (project) {
          setShowDashboard(false)
          setView(id === 'map' ? 'graph' : id === 'bible' ? 'bible' : 'script')
          setFocusMode(false)
        }
      }}
      onTourStep={handleTourStep}
    /></>
  )

  if (taskStore.page) return <>
    <TasksPage project={project} projects={persistence.projects || []} theme={theme} onThemeChange={onThemeChange}
      onSaveStory={persistence.flush} onOpenTarget={openTaskTarget} onProfile={onProfile}
      backLabel={showDashboard ? 'Back to library' : 'Back to writing'} />
    {workspaceHub}
  </>

  if (showDashboard) {
    return (
      <>
        <ProjectDashboard
          projects={persistence.projects}
          activeProjectId={
            persistence.activeProjectId ??
            readBrowserValue('bramble:last-project')
          }
          syncState={syncState}
          error={routeError || persistence.libraryError}
          theme={theme}
          onThemeChange={onThemeChange}
          onOpenProject={openProject}
          onCreateProject={createProject}
          onImportProject={importProject}
          onExportProject={persistence.projectForExport}
          onDeleteProject={persistence.deleteProject}
          onLogout={signOut}
        />
        {workspaceHub}
      </>
    )
  }

  if (!project) {
    return (
      <main className="app-loading">
        <Sprout size={28} />
        <span>Opening Bramble…</span>
      </main>
    )
  }

  const issues = projectIssues(project)
  const selectedNode =
    project.nodes.find((node) => node.id === selectedNodeId) ?? project.nodes[0]
  const endings = project.nodes.filter((node) => node.kind === 'ending').length

  const syncIcon =
    syncState === 'saved' ? (
      <Check size={13} />
    ) : syncState === 'saving' ? (
      <LoaderCircle className="spin" size={13} />
    ) : syncState === 'offline' ? (
      <CloudOff size={13} />
    ) : syncState === 'conflict' ? (
      <AlertTriangle size={13} />
    ) : (
      <CloudUpload size={13} />
    )

  return (
    <>
      <div
        className={
          'bramble-app writer-app' + (focusMode ? ' is-focus-mode' : '')
        }
      >
        <header className="app-header writer-header">
          <div className="brand-lockup">
            <button
              type="button"
              className="brand-home"
              onClick={showProjectDashboard}
              title="Back to projects"
            >
              <span className="brand-mark">
                <Sprout size={20} />
              </span>
              <strong>
                bramble<span>WRITING STUDIO</span>
              </strong>
            </button>
            <span className="header-divider" />
            <input
              className="header-story-title"
              aria-label="Story title"
              data-live-path={JSON.stringify(['title'])}
              value={project.title}
              placeholder="Untitled story"
              onChange={(event) =>
                commitProject(
                  { ...project, title: event.target.value },
                  'story-title',
                )
              }
            />
          </div>
          <nav className="workspace-switcher" aria-label="Editor view">
            <button
              type="button"
              aria-current={view === 'script' ? 'page' : undefined}
              onClick={() => setView('script')}
              aria-label="Write"
              title="Write · Cmd/Ctrl + 2"
            >
              <BookOpenText size={16} />
              <span>Write</span>
            </button>
            <button
              type="button"
              aria-current={view === 'graph' ? 'page' : undefined}
              onClick={() => {
                setView('graph')
                setFocusMode(false)
              }}
              aria-label="Story map"
              title="Story map · Cmd/Ctrl + 1"
            >
              <GitFork size={16} />
              <span>Story map</span>
            </button>
            <button
              type="button"
              aria-current={view === 'bible' ? 'page' : undefined}
              onClick={() => {
                setView('bible')
                setFocusVariables(false)
                setFocusMode(false)
              }}
              aria-label="Story bible"
              title="Story bible · Cmd/Ctrl + 3"
            >
              <NotebookPen size={16} />
              <span>Story bible</span>
            </button>
          </nav>
          <nav className="header-actions" aria-label="Project actions">
            <span data-room-tools />
            <button
              type="button"
              className={'sync-chip sync-chip--' + syncState}
              onClick={() => void persistence.flush()}
              title="Save now · Cmd/Ctrl + S"
              aria-live="polite"
            >
              {syncIcon}
              <span>
                {syncState === 'saved'
                  ? 'All changes saved'
                  : syncState === 'saving'
                    ? 'Saving…'
                    : syncState === 'error'
                      ? 'Needs attention'
                      : syncState}
              </span>
            </button>
            <details className="export-menu">
              <summary title="Export story">
                <Download size={16} />
                <span>Export</span>
                <ChevronDown size={11} />
              </summary>
              <div className="workspace-menu">
                <span>TAKE YOUR STORY WITH YOU</span>
                <button
                  type="button"
                  onClick={(event) => {
                    setReviewExportOpen(true)
                    event.currentTarget
                      .closest('details')
                      ?.removeAttribute('open')
                  }}
                >
                  <FileText size={15} />
                  <span>
                    Review copy<small>Word document · Print / PDF</small>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    setProductionExportOpen(true)
                    event.currentTarget
                      .closest('details')
                      ?.removeAttribute('open')
                  }}
                >
                  <Download size={15} />
                  <span>
                    Production handoff
                    <small>Dialogue, localization & game data</small>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    downloadManuscript(project)
                    event.currentTarget
                      .closest('details')
                      ?.removeAttribute('open')
                  }}
                >
                  <FileText size={15} />
                  <span>
                    Readable draft<small>Plain text · .txt</small>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    downloadManuscript(project, true)
                    event.currentTarget
                      .closest('details')
                      ?.removeAttribute('open')
                  }}
                >
                  <FileText size={15} />
                  <span>
                    Markdown draft<small>Formatted text · .md</small>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    void downloadBackup(project)
                    event.currentTarget
                      .closest('details')
                      ?.removeAttribute('open')
                  }}
                >
                  <Download size={15} />
                  <span>
                    Project backup<small>Complete story & bible · .json</small>
                  </span>
                </button>
              </div>
            </details>
            <button
              type="button"
              className="button play-button"
              aria-label="Playtest"
              title="Play from the opening"
              onClick={() => {
                setPreviewStartId(project.startNodeId)
                setPreviewOpen(true)
              }}
            >
              <Play size={13} fill="currentColor" />
              <span>Playtest</span>
            </button>
          </nav>
        </header>

        <div className="save-notices" aria-label="Save and recovery notices">
          {syncState === 'conflict' && (
            <div className="conflict-banner" role="alert">
              <AlertTriangle size={17} />
              <span>
                The server and this browser both changed. Choose which complete
                copy to keep.
              </span>
              <button
                type="button"
                onClick={() => void downloadBackup(project)}
              >
                Download this draft
              </button>
              <button
                type="button"
                onClick={() => {
                  persistence.useServerCopy()
                  pastRef.current = []
                  futureRef.current = []
                }}
              >
                Use server copy
              </button>
              <button type="button" onClick={persistence.keepBrowserCopy}>
                Keep this browser copy
              </button>
            </div>
          )}

          {!persistence.cacheAvailable &&
            syncState !== 'conflict' &&
            !(
              (syncState === 'error' || syncState === 'offline') &&
              persistence.syncError
            ) && (
              <div className="conflict-banner" role="alert">
                <AlertTriangle size={17} />
                <span>
                  {syncState === 'saved'
                    ? 'Saved to your library. This browser cannot keep an offline recovery copy.'
                    : 'Browser recovery is unavailable. Keep this tab open until your draft saves, or download a backup.'}
                </span>
                <button
                  type="button"
                  onClick={() => void downloadBackup(project)}
                >
                  Download backup
                </button>
              </div>
            )}

          {(syncState === 'error' || syncState === 'offline') &&
            persistence.syncError && (
              <div className="conflict-banner" role="alert">
                <CloudOff size={17} />
                <span>
                  {persistence.syncError}
                  {!persistence.cacheAvailable &&
                    !persistence.syncError
                      .toLowerCase()
                      .includes('browser recovery') &&
                    ' Browser recovery is unavailable; keep this tab open or download a backup.'}
                </span>
                {!persistence.cacheAvailable && (
                  <button
                    type="button"
                    onClick={() => void downloadBackup(project)}
                  >
                    Download backup
                  </button>
                )}
                {persistence.canRecoverProject ? (
                  <button
                    type="button"
                    onClick={() => void persistence.recoverProject()}
                  >
                    Recover as new project
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void persistence.flush()}
                  >
                    Retry now
                  </button>
                )}
              </div>
            )}
        </div>

        <div
          className={
            'app-workspace writer-workspace' +
            (inspectorOpen && view !== 'bible' ? ' has-inspector' : '') +
            (outlineOpen ? ' outline-is-open' : '')
          }
        >
          {outlineOpen && (
            <button
              type="button"
              className="outline-backdrop"
              aria-label="Close scene outline"
              onClick={() => setOutlineOpen(false)}
            />
          )}
          <aside
            className="outline-panel writer-outline"
            aria-label="Scene outline"
          >
            <div className="outline-project">
              <button type="button" onClick={showProjectDashboard}>
                <LayoutGrid size={14} /> All projects
              </button>
              <button
                type="button"
                className="icon-button mobile-outline-close"
                aria-label="Close scene outline"
                onClick={() => setOutlineOpen(false)}
              >
                <X size={15} />
              </button>
            </div>
            <header>
              <div>
                <span>MANUSCRIPT</span>
                <small>
                  {project.nodes.length} scenes · {endings}{' '}
                  {endings === 1 ? 'ending' : 'endings'}
                </small>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  addNode()
                  setView('script')
                  setOutlineOpen(false)
                }}
                title="Add scene"
              >
                <Plus size={17} />
              </button>
            </header>
            <label className="outline-search">
              <Search size={14} />
              <input
                ref={searchRef}
                aria-label="Search your story"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search your story…"
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear story search"
                  onClick={() => setSearch('')}
                >
                  <X size={12} />
                </button>
              ) : (
                <kbd>⌘K</kbd>
              )}
            </label>
            <div className="outline-filter">
              <select
                aria-label="Filter scenes by draft status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All scenes</option>
                {draftStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <span>
                {filteredNodes.length} / {project.nodes.length}
              </span>
            </div>
            <div className="outline-list">
              {filteredNodes.map((node, index) => {
                const nodeIssues = issues.filter(
                  (issue) => issue.nodeId === node.id && issue.kind === 'flow',
                ).length
                const kind =
                  node.kind === 'ending'
                    ? 'ending'
                    : node.choices.length
                      ? 'choice'
                      : 'scene'
                const chapter = node.chapter?.trim() || 'Your scenes'
                const previousChapter = index
                  ? filteredNodes[index - 1].chapter?.trim() || 'Your scenes'
                  : null
                const sceneNumber = project.nodes.indexOf(node) + 1
                return (
                  <div key={node.id}>
                    {chapter !== previousChapter && (
                      <div className="outline-chapter">{chapter}</div>
                    )}
                    <button
                      type="button"
                      className={
                        'outline-scene' +
                        (selectedNodeId === node.id ? ' is-selected' : '')
                      }
                      aria-current={
                        selectedNodeId === node.id ? 'true' : undefined
                      }
                      onClick={() => selectScene(node.id)}
                      onDoubleClick={() => setView('script')}
                    >
                      <span className="outline-index">
                        {String(sceneNumber).padStart(2, '0')}
                      </span>
                      <span className="outline-copy">
                        <strong>{node.title || 'Untitled scene'}</strong>
                        <small>
                          <i data-status={node.status ?? 'draft'} />
                          {
                            draftStatuses.find(
                              (status) =>
                                status.value === (node.status ?? 'draft'),
                            )?.label
                          }
                          <span>·</span>
                          {nodeWordCount(node)} words
                        </small>
                      </span>
                      {nodeIssues > 0 ? (
                        <span
                          className="outline-warning"
                          title={nodeIssues + ' story flow notes'}
                        >
                          {nodeIssues}
                        </span>
                      ) : kind === 'choice' ? (
                        <GitFork size={14} className="outline-type-icon" />
                      ) : kind === 'ending' ? (
                        <Flag size={13} className="outline-type-icon" />
                      ) : node.id === project.startNodeId ? (
                        <span className="opening-dot" title="Opening scene" />
                      ) : null}
                    </button>
                  </div>
                )
              })}
              {!filteredNodes.length && (
                <div className="outline-empty">
                  <Search size={23} />
                  <strong>No scenes found</strong>
                  <p>Try another word or draft status.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('')
                      setStatusFilter('all')
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
              <button
                type="button"
                className="outline-add-scene"
                onClick={() => {
                  addNode()
                  setView('script')
                  setOutlineOpen(false)
                }}
              >
                <Plus size={15} /> New scene <kbd>N</kbd>
              </button>
            </div>
            <div className="writing-goal">
              <div>
                <span>MANUSCRIPT GOAL</span>
                <label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    aria-label="Manuscript word goal"
                    placeholder="Set goal"
                    value={project.wordGoal || ''}
                    onChange={(event) =>
                      commitProject(
                        {
                          ...project,
                          wordGoal: Math.max(
                            0,
                            Number(event.target.value) || 0,
                          ),
                        },
                        'word-goal',
                      )
                    }
                  />
                  <span>words</span>
                </label>
              </div>
              <strong>
                {storyWordCount(project).toLocaleString()}
                <small>
                  {project.wordGoal
                    ? ' / ' + project.wordGoal.toLocaleString() + ' words'
                    : ' words written'}
                </small>
              </strong>
              {Boolean(project.wordGoal) && (
                <progress
                  aria-label="Manuscript goal progress"
                  max={project.wordGoal}
                  value={Math.min(storyWordCount(project), project.wordGoal!)}
                />
              )}
              <p>
                {project.wordGoal && storyWordCount(project) >= project.wordGoal
                  ? 'You reached your goal. A good day’s writing.'
                  : 'One scene at a time.'}
              </p>
            </div>
            <div className="outline-bottom">
              <button
                type="button"
                onClick={() =>
                  onThemeChange(theme === 'dark' ? 'light' : 'dark')
                }
                title={
                  'Switch to ' +
                  (theme === 'dark' ? 'light' : 'dark') +
                  ' theme'
                }
              >
                {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
              </button>
              <button
                type="button"
                title="Log out"
                onClick={() => void signOut()}
              >
                <LogOut size={15} />
              </button>
            </div>
          </aside>

          <main className="editor-stage">
            <div className="workspace-contextbar">
              <button
                type="button"
                className="mobile-outline-toggle"
                onClick={() => setOutlineOpen(!outlineOpen)}
              >
                <PanelLeft size={16} /> Scenes
              </button>
              <span>
                <i className="workspace-live-dot" />
                {view === 'script'
                  ? 'Your writing desk'
                  : view === 'graph'
                    ? 'Every choice, connected'
                    : 'Your story’s foundations'}
              </span>
              <div>
                <button
                  type="button"
                  className="inspector-toggle"
                  onClick={() => setFindOpen(true)}
                  title="Find & replace · Cmd/Ctrl + F"
                  aria-label="Find & replace"
                >
                  <Search size={15} />
                  <span>Find</span>
                </button>
                <button
                  type="button"
                  className="inspector-toggle"
                  onClick={async () => {
                    await persistence.flush()
                    setHistoryOpen(true)
                  }}
                  title="Saved draft history"
                  aria-label="History"
                >
                  <History size={15} />
                  <span>History</span>
                </button>
                <button
                  type="button"
                  className="icon-button"
                  disabled={persistence.isLive ? !persistence.liveHistory.undo : !pastRef.current.length}
                  onClick={undo}
                  title="Undo"
                >
                  <Undo2 size={15} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  disabled={persistence.isLive ? !persistence.liveHistory.redo : !futureRef.current.length}
                  onClick={redo}
                  title="Redo"
                >
                  <Redo2 size={15} />
                </button>
                {view !== 'bible' && (
                  <button
                    type="button"
                    className={
                      'inspector-toggle' + (inspectorOpen ? ' is-active' : '')
                    }
                    aria-pressed={inspectorOpen}
                    onClick={() => setInspectorOpen(!inspectorOpen)}
                    title="Toggle scene inspector"
                  >
                    <PanelRight size={15} />
                    <span>Inspector</span>
                  </button>
                )}
              </div>
            </div>
            {view === 'graph' ? (
              <GraphWorkspace
                onReturnToWriting={() => setView('script')}
                onSaveAndReload={async () => {
                  if (await persistence.flush()) window.location.reload()
                }}
                project={project}
                selectedNodeId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
                onMoveNode={moveNode}
                onConnect={connectNodes}
                onDisconnect={disconnectEdge}
                onAddNode={addNode}
                onAutoLayout={arrange}
                onDeleteNodes={deleteNodes}
                onDeleteElements={(nodeIds, edgeIds) => {
                  const next = removeStoryElements(project, nodeIds, edgeIds)
                  commitProject(next, 'delete-graph-elements')
                  if (!next.nodes.some((node) => node.id === selectedNodeId))
                    setSelectedNodeId(next.startNodeId)
                }}
                onSetOpening={(nodeId) =>
                  commitProject(
                    { ...project, startNodeId: nodeId },
                    'set-opening',
                  )
                }
                onEditNode={(nodeId) => {
                  setSelectedNodeId(nodeId)
                  setView('script')
                }}
              />
            ) : view === 'bible' ? (
              <StoryBible
                tourStep={bibleTourStep}
                focusTarget={bibleFocus}
                onSaveStory={persistence.flush}
                onOpenReference={openReference}
                project={project}
                onChange={commitProject}
                focusVariables={focusVariables}
                onSelectNode={(nodeId) => {
                  setSelectedNodeId(nodeId)
                  setView('script')
                  setInspectorOpen(true)
                  setInspectorTab('details')
                }}
              />
            ) : (
              <ScreenplayEditor
                onSaveStory={persistence.flush}
                onOpenReference={openReference}
                project={project}
                selectedNodeId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
                onChange={commitProject}
                onAddNode={() => addNode()}
                focusMode={focusMode}
                onToggleFocus={() => setFocusMode(!focusMode)}
                onOpenVariables={openVariables}
              />
            )}
          </main>
          {inspectorOpen && view !== 'bible' && (
            <StoryInspector
              onOpenReference={openReference}
              key={selectedNode.id}
              project={project}
              node={selectedNode}
              tab={inspectorTab}
              onTabChange={setInspectorTab}
              onChange={commitProject}
              onSelectNode={(nodeId) => {
                selectScene(nodeId)
                setView('script')
              }}
              onDeleteNode={deleteNode}
              onClose={() => setInspectorOpen(false)}
              onOpenVariables={openVariables}
            />
          )}
        </div>

        <footer className="status-bar writer-statusbar">
          <span className={'status-sync status-sync--' + syncState}>
            {syncState === 'saved' ? (
              <Check size={12} />
            ) : (
              <CloudOff size={12} />
            )}
            {syncState === 'saved'
              ? 'Saved to your library'
              : syncState === 'saving'
                ? 'Saving your work…'
                : syncState === 'offline'
                  ? persistence.cacheAvailable
                    ? 'Offline · browser recovery active'
                    : 'Offline · download a backup'
                  : syncState === 'conflict'
                    ? 'Resolve the save conflict above'
                    : 'Your save needs attention'}
          </span>
          <button
            type="button"
            className="story-review-link"
            onClick={() => {
              setInspectorOpen(true)
              setInspectorTab('review')
              if (view === 'bible') setView('script')
              setFocusMode(false)
            }}
          >
            <CheckCheck size={13} />
            {issues.length
              ? issues.length +
                (issues.length === 1 ? ' review note' : ' review notes')
              : 'Story flow looks good'}
          </button>
          <span className="status-selection">
            {selectedNode.title} · {nodeWordCount(selectedNode)} words
          </span>
          <button
            type="button"
            className="play-scene-link"
            onClick={() => {
              setPreviewStartId(selectedNode.id)
              setPreviewOpen(true)
            }}
          >
            <Play size={12} /> Play this scene
          </button>
          <details className="shortcut-menu">
            <summary aria-label="Keyboard shortcuts" title="Keyboard shortcuts">
              <HelpCircle size={14} />
            </summary>
            <div className="workspace-menu">
              <span>KEYBOARD SHORTCUTS</span>
              <p>
                Find a scene <kbd>⌘/Ctrl K</kbd>
              </p>
              <p>
                Find & replace <kbd>⌘/Ctrl F</kbd>
              </p>
              <p>
                Save <kbd>⌘/Ctrl S</kbd>
              </p>
              <p>
                Undo / redo <kbd>⌘/Ctrl Z / ⇧Z</kbd>
              </p>
              <p>
                Focus mode <kbd>⌘/Ctrl ⇧F</kbd>
              </p>
              <p>
                New block <kbd>⌘/Ctrl Enter</kbd>
              </p>
              <p>
                New dialogue <kbd>⌘/Ctrl ⇧Enter</kbd>
              </p>
              <p>
                New scene <kbd>N</kbd>
              </p>
              <p>
                Map / Write / Bible <kbd>⌘/Ctrl 1 / 2 / 3</kbd>
              </p>
            </div>
          </details>
        </footer>

        {biblePeek && (
          <BiblePeek
            onSaveStory={persistence.flush}
            project={project}
            targetId={biblePeek}
            onChange={commitProject}
            onOpen={openReference}
            onClose={() => setBiblePeek(null)}
          />
        )}
        {previewOpen && (
          <PlayPreview
            project={project}
            startNodeId={previewStartId}
            onClose={() => setPreviewOpen(false)}
            onEditNode={(nodeId) => {
              setPreviewOpen(false)
              setSelectedNodeId(nodeId)
              setView('script')
            }}
          />
        )}
        {historyOpen && (
          <DraftHistory
            userId={user.id}
            project={project}
            onClose={() => setHistoryOpen(false)}
            onCreate={persistence.createNamedDraft}
            onRestore={importProject}
            onUnauthorized={persistence.handleUnauthorized}
          />
        )}
        {productionExportOpen && (
          <ProductionExport
            project={project}
            selectedNodeId={selectedNode.id}
            onClose={() => setProductionExportOpen(false)}
            onReview={() => {
              setProductionExportOpen(false)
              setView('script')
              setInspectorTab('review')
              setInspectorOpen(true)
            }}
          />
        )}
        {findOpen && (
          <FindReplace
            project={project}
            selectedNodeId={selectedNode.id}
            onChange={commitProject}
            onClose={() => setFindOpen(false)}
            onOpenMatch={(match) => {
              setFindOpen(false)
              setView('script')
              setSelectedNodeId(match.nodeId)
              if (['chapter', 'synopsis', 'notes'].includes(match.field)) {
                setInspectorOpen(true)
                setInspectorTab('details')
              }
              setPendingMatch(match)
            }}
          />
        )}
        {reviewExportOpen && (
          <ReviewExport
            project={project}
            selectedNodeId={selectedNode.id}
            onClose={() => setReviewExportOpen(false)}
          />
        )}
      </div>
      {workspaceHub}
    </>
  )
}
